import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';

import { forkJoin } from 'rxjs';

import { OrderMenuItemSourceType } from '../../core/enums/order-menu-item-source-type.enum';
import { WarningSeverity } from '../../core/enums/warning-severity.enum';
import { WarningType } from '../../core/enums/warning-type.enum';

import { Address } from '../../core/models/address.model';
import { CartValidatedItem, CartValidatedVendor } from '../../core/models/cart-response.model';
import { CartItemAddOn, CartItemOption } from '../../core/models/cart.model';
import { NewCartItem } from '../../core/models/new-cart-item.model';

import { AnalyticsService } from '../../core/services/analytics.service';
import { CartService } from '../../core/services/cart.service';
import { DeepLinkService } from '../../core/services/deep-link.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalService } from '../../core/services/modal.service';
import { OrdersService } from '../../core/services/orders.service';
import { OverlayService } from '../../core/services/overlay.service';
import { ValidationService } from '../../core/services/validation.service';
import { VendorsService } from '../../core/services/vendors.service';

import { PreviousOrderModalPageAnimations } from './previous-order-modal.animations';

export const PreviousOrderModalPageIdentifier = 'previous-order-modal';

interface ItemAvailabilityDetails {
  available: boolean;
  femaleServiceAvailable: boolean;
  optionsAvailability: Record<number, boolean>;
  addonsAvailability: Record<number, boolean>;
}

@Component({
  selector: 'app-previous-order-modal',
  templateUrl: './previous-order-modal.page.html',
  styleUrls: ['./previous-order-modal.page.scss'],
  animations: [...PreviousOrderModalPageAnimations],
  encapsulation: ViewEncapsulation.None,
})
export class PreviousOrderModalPage implements OnInit {
  @Input()
  public ordersVendors: Array<{ orderId: number; vendorId: number }>;

  @Input()
  public selectedAddress?: Address;

  @Input()
  public selectedDateTime?: string;

  @Input()
  public isOrderForNowSelected?: boolean;

  public isLoading: boolean;
  public hasAvailableItems: boolean;
  public cartTotal: number;
  public cartVendors: Array<CartValidatedVendor>;
  public itemsAvailabilityDetails: Record<number, ItemAvailabilityDetails>;

  constructor(
    private cartService: CartService,
    private modalService: ModalService,
    private ordersService: OrdersService,
    private deepLinkService: DeepLinkService,
    private validationService: ValidationService,
    private overlayService: OverlayService,
    private loggerService: LoggerService,
    private vendorsService: VendorsService,
    private analyticsService: AnalyticsService,
  ) {}

  ngOnInit() {
    this.initializePage();
  }

  public onAddToCart(): void {
    const allItems = this.cartVendors.reduce(
      (pv, v) => pv.concat(v.items.map((i) => ({ cartVendor: v, cartItem: i }))),
      [] as Array<{ cartVendor: CartValidatedVendor; cartItem: CartValidatedItem }>,
    );

    const newCartItems = allItems
      .filter((ci) => this.itemsAvailabilityDetails[ci.cartItem.menuItemId].available)
      .map((cartVendorItem) => {
        const { cartVendor, cartItem } = cartVendorItem;
        const itemAvailabilityDetails = this.itemsAvailabilityDetails[cartItem.menuItemId];

        const selectedOptions: Array<CartItemOption> = cartItem.options?.length
          ? cartItem.options.filter((option) => option.quantity > 0 && itemAvailabilityDetails.optionsAvailability[option.menuItemOptionId])
          : undefined;

        const selectedAddOns: Array<CartItemAddOn> = cartItem.addOns?.length
          ? cartItem.addOns.filter((addOn) => addOn.quantity > 0 && itemAvailabilityDetails.addonsAvailability[addOn.menuItemAddOnId])
          : undefined;

        const itemServiceTypeRequirements = cartVendor.serviceTypeRequirements.find(
          (requirement) => requirement.serviceType === cartItem.serviceType,
        );

        const orderId = this.ordersVendors.find((orderVendor) => orderVendor.vendorId === cartVendor.catererId).orderId;

        return {
          catererId: cartItem.catererId,
          menuItemId: cartItem.menuItemId,
          menuItemName: cartItem.name,
          serviceType: cartItem.serviceType,
          imageUrl: cartItem.imageUrl,
          isBilbaytNow: cartItem.canOrderForNow,
          specialRequests: cartItem.specialRequests,
          femaleService:
            cartItem.femaleServers && this.itemsAvailabilityDetails[cartItem.menuItemId].femaleServiceAvailable ? true : undefined,
          sourceId: orderId,
          sourceType: OrderMenuItemSourceType.ReOrder,
          total: cartItem.total,
          quantity: cartItem.quantity,
          selectedOptions,
          selectedAddOns,
          catererName: cartVendor.name,
          minimumOrderValue: itemServiceTypeRequirements?.minimumOrderAmount || 0,
          noticePeriod: cartItem.noticePeriod,
        };
      });

    let allItemsAddedToCart = true;

    const addToCart = (items: Array<NewCartItem>): Promise<void> => {
      if (items.length < 1) {
        return;
      }

      const [item] = items.splice(0, 1);

      const vendorId = item.catererId;
      const itemId = item.menuItemId;
      const amount = item.total;
      const serviceType = item.serviceType;

      this.analyticsService.trackMixpanelAddToCartAttempt({
        vendorId,
        itemId,
        amount,
        serviceType,
        description: 'User clicked on add to cart button from previous order modal page',
      });

      return this.cartService
        .maybeAddToCart({
          item,
          address: this.selectedAddress ?? this.vendorsService.getAddressSearchFilters(),
          dateTime: this.selectedDateTime,
          isForNow: this.isOrderForNowSelected,
        })
        .then(({ added, failureReason }) => {
          if (!added) {
            this.analyticsService.trackMixpanelAddToCartFailure({ vendorId, itemId, amount, serviceType, description: failureReason });
            allItemsAddedToCart = false;
            return;
          }

          this.analyticsService.trackMixpanelAddToCartSuccess({ vendorId, itemId, amount, serviceType });
          return addToCart(items);
        });
    };

    void addToCart(newCartItems).then(() => {
      if (allItemsAddedToCart) {
        // When calling this modal from the order details modal
        // we need to close both modals before redirecting to
        // the cart
        void this.modalService.dismissAllModals().then(() => {
          this.deepLinkService.openCartPage();
        });
      }
    });
  }

  private initializePage(): void {
    this.isLoading = true;

    forkJoin(this.ordersVendors.map(({ orderId, vendorId }) => this.ordersService.getVendorOrderDetails(orderId, vendorId))).subscribe({
      next: (results) => {
        let cartTotal = 0;
        const allCartVendors: Array<CartValidatedVendor> = [];
        const itemsAvailabilityDetails: Record<number, ItemAvailabilityDetails> = {};

        results.forEach((cartResponse) => {
          for (const cartItem of cartResponse.caterers[0].items) {
            const optionsAvailability: Record<number, boolean> = {};
            const addonsAvailability: Record<number, boolean> = {};

            if (cartItem.options?.length) {
              for (const option of cartItem.options) {
                optionsAvailability[option.menuItemOptionId] = option.warnings
                  ? !option.warnings.find((warning) => warning.severity === WarningSeverity.Critical)
                  : true;
              }
            }

            if (cartItem.addOns?.length) {
              for (const addon of cartItem.addOns) {
                addonsAvailability[addon.menuItemAddOnId] = addon.warnings
                  ? !addon.warnings.find((warning) => warning.severity === WarningSeverity.Critical)
                  : true;
              }
            }

            const femaleServiceAvailable =
              cartItem.femaleServers && cartItem.warnings
                ? !cartItem.warnings.find((warning) => warning.type === WarningType.UnavailableFemaleService)
                : true;

            const criticalWarningsBesidesUnavailableFemaleService = cartItem.warnings
              ? cartItem.warnings.filter(
                  (warning) => warning.severity === WarningSeverity.Critical && warning.type !== WarningType.UnavailableFemaleService,
                )
              : [];

            itemsAvailabilityDetails[cartItem.menuItemId] = {
              available: cartItem.total !== 0 && criticalWarningsBesidesUnavailableFemaleService.length === 0,
              optionsAvailability,
              addonsAvailability,
              femaleServiceAvailable,
            };
          }

          cartTotal += cartResponse.totalPrice;
          allCartVendors.push(cartResponse.caterers[0]);
        });

        this.cartTotal = cartTotal;
        this.cartVendors = allCartVendors;
        this.itemsAvailabilityDetails = itemsAvailabilityDetails;
        this.hasAvailableItems = this.cartVendors.some((cartVendor) =>
          cartVendor.items.some((i) => this.itemsAvailabilityDetails[i.menuItemId].available),
        );

        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        const errorMessage = this.validationService.getErrorMessage(error);
        this.loggerService.info({
          component: 'PreviousOrderModalPage',
          message: "couldn't get reorder data",
          details: { error, messageShownToUser: errorMessage },
        });
        void this.modalService.dismissModal({ id: PreviousOrderModalPageIdentifier });
        void this.overlayService.showToast({ message: errorMessage, showCloseButton: true, type: 'error' });
      },
    });
  }
}
